/*!
 * sentry-init.js — extracted 2026-07-08 from resources/views/layouts/app.blade.php
 * (the inline "if (window.Sentry) { Sentry.init({...}) }" block with the full
 * ignoreErrors / denyUrls / beforeSend noise gates + the expected-error sink).
 *
 * Loaded DEFERRED (2026-08-06) right after vendor/errtrace/bundle.min.js and
 * js/sentry-noise-filter.js. Deferred scripts execute in document order, so the
 * SDK and the noise gate are always present by the time this runs. It used to be
 * a SYNC tag, and all three together gated first paint on every cold visit —
 * measured on prod /ru, 3 cold runs, median: FCP 2516 → 1568 ms with them
 * blocked; locally under emulated 3G, 3328 → 1644 ms sync → defer. What defer
 * costs is early-error visibility (this file now runs after every inline script
 * on the page), and that is bought back by the replay drain near the bottom —
 * see the layout's window.__earlyErrQ. Both halves are held by
 * tests/Feature/SentryDeferredLoadingTest.php and
 * tests/playwright/head-render-blocking.mjs. (That bundle's directory was named
 * after the vendor until 2026-07-30; renamed because adblock filter lists key on
 * that directory name. This file's own name is fine — telemetry showed it is not
 * filtered.)
 *
 * Expects this inline-shim global (set by the layout right before this tag):
 *   window.__SENTRY_CFG = { release: 'timbrica@<release_id>', env: '<app env>' }
 *   (was Blade release_id() / app()->environment() interpolations).
 */
    if (window.Sentry) {
        // Per-request values injected by the inline Blade shim in the layout.
        var _cfg = window.__SENTRY_CFG || {};
        // Trial-period observability (Performance + Session Replay).
        // Sentry am3_t trial ends 2026-05-14 13:32 UTC; after that the free
        // Developer plan rejects perf/replay events. Auto-disable on that
        // date so the SDK stops paying CPU+bandwidth to record events that
        // would just 429 server-side. Captured data remains queryable in
        // Sentry until ~2026-06-13 (30-day retention from last sent event).
        // After 2026-05-14 the whole `_trial*` block becomes no-op and the
        // helper variables can be deleted in a cleanup commit.
        var _trialEnd = new Date('2026-05-14T13:32:52Z').getTime();
        var _onTrial = Date.now() < _trialEnd;
        // Performance traces: 5% of transactions sampled. Volume is fine
        // (~30K page views/day × 5% × 13 days = ~200K traces, well under 1M
        // trial quota). Sufficient density for percentile aggregations
        // ("p95 of /{locale}/audio-to-text") via the Performance API.
        var _trialTraces  = _onTrial ? 0.05 : 0;
        // Replay records nothing for "happy" sessions — circular buffer is
        // dropped unless an error fires, then last ~30 sec before + window
        // after gets sent. Healthy traffic never recorded. Keeps replay
        // quota minimal AND ensures every captured replay is debug-relevant.
        // Replay disabled: our self-hosted bundle.min.js (74 KB) is the basic
        // build without the Replay class, only the integration *function name*.
        // Calling it produced "You are using replayIntegration() even though
        // this bundle does not include replay" warnings on every page. To
        // actually use Replay we'd need to swap to bundle.tracing.replay.min.js
        // (~150 KB, +80 KB on every page for ~10 days until trial ends).
        // Not worth the extra payload for trial-window observability we won't
        // actively review — Performance traces are sufficient.
        // After 2026-05-14 this whole block becomes no-op anyway.

        // Sentry-filter kill-switch — when triaging a real bug we suspect
        // is being silently dropped by `ignoreErrors`/`denyUrls`, opening
        // the page with `?sentry=debug` (or setting localStorage
        // `__sentry_debug=1` from devtools) bypasses both lists for the
        // session. All events flow to Sentry unfiltered. Used by ourselves;
        // real users never hit this. Console marker confirms it engaged.
        // Does any frame of the event's exception come from a file matching `re`?
        function eventTouches(event, re) {
            var vals = event && event.exception && event.exception.values;
            if (!vals) return false;
            for (var i = 0; i < vals.length; i++) {
                var fr = vals[i] && vals[i].stacktrace && vals[i].stacktrace.frames;
                if (!fr) continue;
                for (var k = 0; k < fr.length; k++) { if (fr[k] && re.test(String(fr[k].filename || ''))) return true; }
            }
            return false;
        }
        var _sentryDebug = false;
        try {
            _sentryDebug = (new URL(location.href)).searchParams.get('sentry') === 'debug'
                        || (window.localStorage && localStorage.getItem('__sentry_debug') === '1');
            if (_sentryDebug) console.info('[Sentry] debug mode — ignoreErrors/denyUrls disabled for this session');
        } catch(_){}

        // Only ship events from OUR origin under http(s). Two structural
        // noise classes get filtered at the SDK boundary so they never
        // even enter the pipeline:
        //   1. file:// / chrome-extension:// / moz-extension:// / blob: /
        //      data: — saved-page mirrors and extension-injected contexts.
        //      Sentry's cross-origin window.onerror muzzles these to "Script
        //      error" / "SyntaxError: Unexpected token '%'" with 0 stack
        //      frames. Useless signal. Sentry TIMBRICA-FRONTEND-3X/42/44/46/…
        //      (15 issues / 37 events from one Edge/Windows host serving
        //      E:\MyWebSites\Sokoban\timbrica.com\* off disk).
        //   2. Anonymizing proxies and offline scrapers that re-serve our
        //      HTML under their own hostname (e.g. Codeproxy via
        //      `https://51.159.111.23/ar/mic-test?__cpo=<b64-of-timbrica-url>`
        //      throwing "__cpLocation is not defined" — Sentry TIMBRICA-FRONTEND-4F).
        //      Their re-served pages still execute our Sentry init, but
        //      `location.hostname` is theirs — we don't want their plumbing
        //      errors paged. In production, restrict to OUR two domains.
        //      In non-prod env (local/staging) the hostname gate is bypassed
        //      so dev events still flow to whichever environment tag.
        //   ⚠️ The gate was written 14.05.2026, when timbrica.com was the only
        //      domain, and named only it. tembrica.com (8 locales, since 12.06)
        //      fell outside it: 90 days to 25.09.2026 — 796 events from
        //      timbrica.com, 4 from tembrica.com. The whole intl frontend was
        //      unwatched, which the gate's own intent («not a proxy») never
        //      asked for.
        var _httpOrigin = (location.protocol === 'https:' || location.protocol === 'http:');
        var _envIsProd = ((_cfg.env || '') === 'production');
        var _hostIsOurs = !_envIsProd
            || /(?:^|\.)t[ie]mbrica\.com$/i.test(location.hostname || '');

        Sentry.init({
            // navigator.webdriver=true under any W3C-spec automation
            // (Playwright, Puppeteer, Selenium). Real users always have
            // it undefined/false. Skipping init at the source means
            // synthetic regression tests never ship events — no need for
            // per-test page.route Sentry blockers.
            enabled: !navigator.webdriver && _httpOrigin && _hostIsOurs,
            dsn: 'https://fba196d8dde8a117a5e3de73ea0d6976@o4511309227819008.ingest.de.sentry.io/4511309482491984',
            release: _cfg.release,
            environment: _cfg.env,
            tracesSampleRate: _trialTraces,
            // Every error, not half (29.10.2026). Almost every issue here is one or two
            // people; at 0.5 every second rare breakage never arrived. Measured volume:
            // 562 frontend + 95 server events in 30 days at 0.5 — ~1.2k a month at 1.0
            // against the plan's 5k.
            sampleRate: 1.0,
            ignoreErrors: _sentryDebug ? [] : [
                /ResizeObserver loop limit exceeded/,
                /Non-Error promise rejection captured/,
                /Script error\.?$/,
                /Failed to fetch dynamically imported module/,
                /Importing a module script failed/,
                // play()/pause() Promise rejections — browser-internal race, never
                // actionable from our code. Covers "user aborted", "pause()",
                // "end of playback", "background media paused to save power",
                // "removing the media element", etc.
                /AbortError/,
                // enumerateDevices() — user denied mic permission OR no devices
                /enumerateDevices/i,
                // Яндекс.Советник и подобные расширения-«помощники покупок»
                // внедряют свой код ПРЯМО В СТРАНИЦУ, а не подключают его с
                // chrome-extension:// — поэтому фильтры по URL выше их не
                // видят: в стектрейсе стоит наш собственный адрес страницы.
                // Единственная надёжная примета — их собственные поля.
                // Разобрано 08.08.2026: 4 события на /ru/iq-test, iOS WKWebView,
                // а в последнем следе перед падением — запрос к
                // sovetnik.market.yandex.ru. Наш канал ошибок не должен
                // сообщать о чужих багах, которые мы не можем починить.
                /evaluating 'e\.domainName'/,
                /\bdomainName\b.*(?:null|undefined)/i,
                // Browser without WebAudio API (privacy mode, embedded WebView, etc.)
                // The first regex catches our named error from the timbrica-core.js stub
                // (proper UX path — user sees a toast). Latter two catch the raw
                // TypeError if the stub somehow didn't load before the call site.
                /AudioContextUnavailable/,
                /AudioContext.*(?:undefined|not a constructor|not defined)/i,
                /webkitAudioContext.*not a constructor/i,
                // WebAssembly SIMD pre-flight in ffmpeg-processor.js throws a
                // localized Error when the CPU is pre-2008 (no SIMD). It's an
                // intentional graceful-failure path — user sees a clear "your
                // device is too old" toast. "WebAssembly" + "(SIMD)" parens
                // appear in every locale's message verbatim.
                /WebAssembly.*\(SIMD\)/,
                // Network drops mid-fetch — user's connectivity, not server bug
                /TypeError: Failed to fetch/,
                /NetworkError when attempting/,
                /Load failed/,
                // Google AdSense's own SecurityError when intercepting navigation —
                // their bug in reactive_library_*.js, only fires in Samsung Internet
                /SecurityError.*intercept.*NavigateEvent/i,
                /window\.gtag is not a function/,
                /window\.ym is not a function/,
                /Yandex/i,
                /yastatic/i,
                /adfox/i,
                /googletagmanager/i,
                /chrome-extension:/,
                /moz-extension:/,
                /safari-extension:/,
                /Cannot read properties of null \(reading 'classList'\)/,
                // Playwright smoke testing leak — UtilityScript.evaluate runs
                // inside the page, and a wrong CSS selector mixed with
                // Playwright extensions (`:has-text()`) throws SyntaxError
                // in document.querySelector. Not a real user bug.
                /querySelector.*is not a valid selector/,
                /UtilityScript\.evaluate/,
                // AdSense `TagError: No slot size for availableWidth=0` and friends —
                // emitted by adsbygoogle.js when a slot lays out at 0px width
                // (display:none ancestor, lazy-rendered card, viewport-narrowed).
                // Not actionable from our side: AdSense already retries; the error
                // simply means "no impression this time". Filter at SDK level so
                // it never reaches Sentry.
                /^TagError:/,
                /No slot size for availableWidth/,
                // Permission-class denials from Promise-returning DOM APIs.
                // Mirror of the timbrica-core.js unhandledrejection filter — Sentry
                // SDK's own auto-instrumentation captures these BEFORE our filter
                // gets a chance, so the patterns must be duplicated here too.
                // Class covers: clipboard.writeText (NotAllowedError: Write permission
                // denied), clipboard.readText (Read permission denied),
                // Document is not focused, getUserMedia denied (NotAllowedError),
                // requestFullscreen, navigator.share, wakeLock — any DOM API that
                // can be vetoed by the user, OS, or page state. Real bugs in
                // permission flows surface elsewhere (UI feedback, telemetry).
                /NotAllowedError/,
                /Write permission denied/,
                /Read permission denied/,
                /Document is not focused/,
                /Permission denied by system/,
                /Permission dismissed/,
                /Permissions check failed/,
                /not granted/,
                // Android WebView without onShowCustomView: requestFullscreen
                // EXISTS but its promise rejects with this exact TypeError
                // (TIMBRICA-FRONTEND-CR, 2026-07-31). Same expected-denial
                // class as the patterns above.
                /Fullscreen is not supported/,
                // A 3rd-party script ELEMENT being inserted whose source fails
                // to COMPILE — the SyntaxError surfaces out of appendChild at
                // the insertion site. OUR pages never appendChild <script>
                // nodes (audited 2026-08-03, TIMBRICA-FRONTEND-CV: 4 users,
                // all Yandex-Browser translate/extension injections on
                // /ru/megaphone). Scoped to the combined signature so a
                // future our-side `new RegExp(str, flags)` bug still pages.
                /appendChild.*Invalid regular expression flags/,
                // In-app-browser bridge scripts (VK/OK class) declare
                // `let nativeIframe` at top level and get injected TWICE →
                // SyntaxError at line 1 col 1 of the page. The identifier
                // does not exist anywhere in our codebase (grepped
                // 2026-08-04, TIMBRICA-FRONTEND-D4) — scoped to that exact
                // name so our own double-declare bugs still page.
                /'nativeIframe' has already been declared/,
                // A FOREIGN request failing inside our XHR interceptor. The
                // telemetry patch on XMLHttpRequest.prototype.send is
                // transparent (it early-returns the original for anything
                // that is not a tool POST), but a synchronous NetworkError
                // from the native send still unwinds THROUGH our frame — so
                // Sentry attributes an antivirus/extension request (e.g.
                // kp.apiget.ru, Kaspersky Protection) to telemetry-tier0.js
                // and denyUrls can never help. Negative lookahead: only drop
                // loads of URLs that are NOT ours; a real failed request to
                // timbrica/tembrica still pages (TIMBRICA-FRONTEND-DJ).
                /Failed to load '(?!https?:\/\/[^']*(?:timbrica\.com|tembrica\.com))/,
                // C1 control character in a script the parser saw. AUDITED
                // from the origin 2026-08-06: the page plus all 61 scripts it
                // loads decode as strict UTF-8 with zero U+009E codepoints, so
                // our origin cannot emit this for anyone — and a malformed
                // served file would break every visitor of the release, not
                // one. Four occurrences in two days across unrelated tools
                // (/trim, /qr-scanner, /video-crop, /subtitle-translator),
                // always one user, always document 1:1: local AV/proxy
                // response mangling or an injected source. Scoped to the C1
                // block so a genuine mojibake bug in our own bytes (which
                // would name a real file and hit everyone) still pages.
                /illegal character U\+00[89][0-9A-F]/i,
                // nginx 499 = the CLIENT closed the request. The user
                // navigated away / cancelled mid-download (here: a 12 MB
                // tesseract language pack). Nothing server-side happened and
                // nothing is actionable (TIMBRICA-FRONTEND-DR).
                /Response code: 499/,
                // html5-qrcode's INTERNAL teardown scream when the video
                // surface aborts (navigation away / camera yanked mid-scan).
                // Barcode-scanner now stops the camera on pagehide/tab-hide,
                // but stop() is async and cannot win every race — and the
                // throw happens deep inside the vendored lib where no call
                // site of ours can catch it (TIMBRICA-FRONTEND-DG).
                /RenderedCameraImpl video surface onabort/,
                // MIUI WebView's Java bridge surfaces inscrutable errors with no
                // useful stack — `<anonymous>:1` everywhere. Not our code to fix.
                /Java exception was raised during method invocation/,
                // Shared-module stub errors. timbrica-core.js's __sharedModule
                // helper installs queue-stubs for ChannelModule / ToolUploader /
                // AudioDecoder / AudioRecover before their real scripts load
                // (defer-onerror race defense, see helper docs). For the
                // Promise-returning methods the stub returns a deferred
                // Promise that rejects with a descriptive error if the real
                // script never arrives within 15s. Those rejections are our
                // OWN defensive surfaces — the tool's catch handler shows
                // user-actionable UX. Filter them at SDK level so they never
                // pollute Sentry, regardless of how loudly they fire (a stale
                // tab with an earlier broken stub variant generated 168
                // events / 32 users in ~4 min on 2026-05-02).
                /failed: module never loaded/,
                /not loaded yet$/,
                // CSP-blocked eval from 3rd-party blobs (AdSense's `tryCatch →
                // Function('return this')` global-this sniff is the dominant
                // culprit). Our CSP correctly blocks `'unsafe-eval'`, the
                // 3rd-party degrades gracefully, and we already log every CSP
                // violation via the report-uri at /api/csp-report — no need
                // to duplicate them as Sentry "errors". Sentry 117017629
                // (2026-05-02): 3 events / 2 users on /ru/add-music-to-video,
                // EvalError culprit `Function(<anonymous>)` with stack inside
                // a minified XHR.onload → debug → tryCatch → Function path.
                /violates the following Content Security Policy/i,
                /'unsafe-eval' is not an allowed source/i,
                // Browser-extension messaging surfaces. Chrome `runtime.sendMessage`
                // / Firefox `browser.runtime.sendMessage` rejections fire on tabs
                // where the receiver script either isn't loaded or got
                // disconnected — never our code, the extension's own state. Sentry
                // 117687765 (Chrome ext, "Could not establish connection."),
                // 117296379 (DuckDuckGo iOS, "Invalid call to runtime.sendMessage()
                // Tab not found").
                /Could not establish connection.*Receiving end does not exist/,
                /Invalid call to runtime\.sendMessage/,
                // chrome.runtime onMessage listener returned `true` (promising an
                // async response) but the tab/extension tore the port down before
                // sendResponse ran. Pure extension-state noise — it surfaces as an
                // unhandledrejection whose top frame is chrome-extension://…sandbox-
                // content.js and whose callback frame is a blob:OUR-origin URL, so
                // the zero-app-frames gate in beforeSend does NOT catch it (the blob
                // counts as in-app). Match the message directly. (TIMBRICA-FRONTEND-AD)
                /A listener indicated an asynchronous response by returning true/,
                /message channel closed before a response was received/,
                // An obfuscated browser-extension content script (function names like
                // `_0x38e2f2`) injected into the page from a blob:OUR-origin URL guards
                // itself with `throw new Error("This script should only be loaded in a
                // browser extension")` when it detects it is NOT in an extension context.
                // Pure extension noise; the blob:our-origin frames slip the zero-app-frames
                // gate, same as the messaging ones above. (TIMBRICA-FRONTEND-AH)
                /This script should only be loaded in a browser extension/,
                // webextension-polyfill (Mozilla's `browser.*` shim) bundled into a
                // browser-extension content script and injected into the page main
                // world via a blob:OUR-origin URL. In page context `chrome.runtime`
                // exposes only the messaging subset, so `runtime.getManifest` is
                // undefined → TypeError. `browser_polyfill_default` is the bundler's
                // import symbol for that package; neither string exists in our
                // codebase (grep 0). blob:our-origin frames count as in-app and slip
                // the zero-app-frames gate, same as AD/AH above. (TIMBRICA-FRONTEND-AN)
                /browser_polyfill_default/,
                /runtime\.getManifest is not a function/,
                // Obfuscated browser-extension content script, ReferenceError
                // flavour: commercial JS obfuscators rename every identifier
                // to _0xHEX — "Uncaught ReferenceError: _0xfd7cc is not
                // defined" from a blob:OUR-origin script on /ru/cpu-benchmark
                // (Chrome, 60 events). Proven not ours (grepped 2026-08-13):
                // `_0x` appears in public/js and the cpu-benchmark blade ONLY
                // inside noise filters/comments — timbrica-core.js:1528
                // already gates this exact identifier class for the OTHER
                // sink (tool_error telemetry via window.onerror); this is its
                // Sentry-side mirror. cpu-benchmark's own blob worker is
                // assembled in getWorkerCode() from readable, unobfuscated
                // source. blob:our-origin frames count as in-app and slip the
                // zero-app-frames gate, same as AD/AH/AN above.
                // (TIMBRICA-FRONTEND-EW)
                /\b_0x[0-9a-f]{4,}\b is not defined/i,
                /Can't find variable: _0x[0-9a-f]{4,}/i,
                // Edge SmartScreen / browser-extension content-script that fails
                // to send tab info before its own WebSocket connects to the
                // extension backend. Stack frames are in `/resources/frontend.min.js`
                // — that's the extension's path, not ours. Sentry 117818321.
                /Failed to execute 'send' on 'WebSocket': Still in CONNECTING state/,
                /Cannot send tab info: web socket not open yet/,
                // DuckDuckGo iOS Privacy Browser injects a content-script that
                // posts back via WKWebView's JS↔native bridge; on tabs where the
                // bridge already torn down, the post rejects unhandled. Their
                // bug, not ours. Sentry 117752741.
                /WKWebView API client did not respond to this postMessage/,
                // [removed 2026-05-18] DuckDuckGo content-scope-scripts
                // "feature named `X` was not found" — now subsumed by the
                // own-frame noise gate in beforeSend (zero app frames →
                // dropped). Sentry TIMBRICA-FRONTEND-5N (120503709).
                // Speechify-like Chrome/Yandex extensions inject a global
                // `remoteFrameRegistration` and crash on certain SPA navigations.
                // Sentry 117558793.
                /remoteFrameRegistration\.processChildFrameMessage/,
                // iOS Safari content blocker / anti-fingerprint userscript on
                // Tor users' devices reads `window.__torBrowser` — a global
                // that the Tor Browser DESKTOP bundle defines, but Mobile
                // Safari + Onion-style iOS browsers do NOT. The variable is
                // not in our codebase (grep 0). Mobile Safari attributes the
                // injected-script ReferenceError to the HTML doc URL via
                // `(global code)` at line 1, which slips past the own-frame
                // noise gate. (TIMBRICA-FRONTEND-8R)
                /Can't find variable: __torBrowser/,
                /__torBrowser is not defined/,
                // WKWebView in-app browsers (iOS) inject `_callback_<name>`
                // bridge globals for their native<->JS shims (WebMIDI polyfill
                // et al.) and then reference them from injected global code —
                // Mobile Safari attributes that to the page URL at :1, past the
                // own-frame gate. `_callback_receiveMIDIMessage` on
                // /ru/online-piano regressed the __torBrowser issue group
                // (TIMBRICA-FRONTEND-8R); the identifier has 0 matches
                // repo-wide (grepped 2026-08-19). Match the bridge PREFIX —
                // same app family injects more than one callback name.
                /Can't find variable: _callback_/,
                /_callback_[A-Za-z]+ is not defined/,
                // iOS Safari content-blocker / extension content script reads
                // its own bootstrap object before the injector defined it —
                // "undefined is not an object (evaluating
                // 'contentScriptData.init_ts')" on the homepage. The
                // identifier `contentScriptData` does not exist anywhere in
                // our codebase (grepped 2026-08-13: 0 matches repo-wide).
                // Mobile Safari attributes injected-script errors to the page
                // URL `(global code)` at line 1, slipping past the own-frame
                // noise gate — same as __torBrowser above.
                // (TIMBRICA-FRONTEND-EM)
                /\bcontentScriptData\b/,
                // Product-scraper / price-tracker browser extensions (Honey,
                // Amazon/eBay sniffers, comparison-shopping sniffers) inject
                // a structured-data selector probe like
                //   [itemtype="http://schema.org/Product"],…,.hProduct,.hproduct
                // on every page they visit. On pages without that markup
                // (every Timbrica tool), the selector throws SyntaxError
                // from a querySelector call. Mobile Safari attributes it
                // to the page URL `(global code)` — slips past the own-
                // frame gate. We never use schema.org in our own code
                // (grep 0). (TIMBRICA-FRONTEND-92)
                /schema\.org\/Product/,
                // Yandex.Direct ads SDK (yandex.ru/ads/system/context.js) on
                // iOS Yandex Browser / WKWebView reads `window.webkit.message
                // Handlers` without optional chaining. The existing fingerprint
                // filter further down in beforeSend requires nativeOnly stack
                // frames (forEach / abs_path = [native code]) — but a click
                // handler attribution puts a named frame at the page URL line
                // 1, slipping past that gate. We never use webkit.messageHandlers
                // in our own code (grep 0), so any error mentioning it is
                // foreign noise. (TIMBRICA-FRONTEND-48 regressed)
                /window\.webkit\.messageHandlers/,
                // postMessage cross-origin handshake from third-party scripts /
                // ad iframes — opaque "Error: invalid origin" with no app frames.
                // Sentry 117402041.
                /^Error: invalid origin$/,
                /^invalid origin$/,
                // SIMD/WebAssembly hardware-incompat errors used to be
                // filtered here (Sentry 117207558 / 117186490). Replaced
                // 2026-05-06 by a clean architecture: ffmpeg-processor.js
                // marks the throw with `err.expected = true`, engine-warmer
                // skips rethrow on expected failures, the rnnoise-realtime
                // init pre-flight bails before vendor abort runs. Result:
                // engine-warmer returns `false` cleanly, no
                // unhandledrejection bubbles up — Sentry never sees it,
                // analytics_events still records the cohort via
                // __aeToolError({reason:'simd_unsupported'|'wasm_unsupported'}).
                // IndexedDB unavailable in private browsing on iOS Safari /
                // some embedded WebViews. timbrica-history graceful-degrades
                // (history feature simply unavailable) — Sentry signal is
                // noise. Sentry 117541575.
                /IDBFactory\.open\(\) called in an invalid security context/,
                /An attempt was made to break through the security policy.*IDB/i,
                // Anti-audio-fingerprint extension (AudioContext Fingerprint
                // Defender class; Yandex Browser Android runs Chrome extensions)
                // redefines AnalyserNode.prototype properties with a getter-only
                // descriptor at document_start. Our synth modules run 'use strict',
                // so `analyser.fftSize = N` throws TypeError instead of the
                // sloppy-mode no-op. Per Web Audio spec every AnalyserNode
                // attribute is read-write in every real browser — getter-only is
                // PROOF of tampering, never our code (grep 0: no AnalyserNode
                // prototype patching; our AC wrappers return native instances).
                // The tamper leaves no frames of its own, stack is all in-app →
                // zero-app-frames gate can't catch it; filter by message (AD/AH
                // precedent). Covers all ~20 fftSize/smoothing call sites at once.
                // (TIMBRICA-FRONTEND-AJ)
                /Cannot set property \w+ of #<AnalyserNode> which has only a getter/,
            ],
            denyUrls: _sentryDebug ? [] : [
                /https:\/\/(yandex|yastatic|adfox|googletagmanager|googlesyndication|google-analytics|googleadservices|gstatic)\./,
                // Google AdSense / Auto Ads — every path under /pagead/ is their
                // code, never ours. Third-party noise (NavigateEvent SecurityError,
                // TagError on zero-width slots, internal rejections, etc.)
                /\/pagead\//,
                // Google AdSense "Content Ads Contributor" / IAB TCF CMP code,
                // hosted under boq-content-ads-contributor. Surfaces as
                // "__tcfapiCall" / "__fciCall is undefined" (Sentry TIMBRICA-
                // FRONTEND-54/55). Different Google subsystem than /pagead/ —
                // the existing pattern doesn't catch it. Same character: not
                // our code, no fix on our side.
                /\/boq-content-ads-contributor\//,
                // "instant-web" / "instantweb" Android browser extension injects
                // its own getSelector/checkLogic on every click. SVGAnimatedString
                // .className.split crash is in their code, not ours.
                // Sentry 117283043.
                /\/instant-web\//,
                /\/instantweb\.min\.js/,
                // Edge SmartScreen / similar browser extension injects scripts
                // under `/resources/frontend.min.js` — never our path (we ship
                // from /js/, /css/, /vendor/, /cdn/). Sentry 117818321.
                /\/resources\/frontend\.min\.js/,
                // ONNX Runtime Web internals (we self-host the vendor build).
                // Its wasm faults ("memory access out of bounds", RuntimeError
                // in ort-wasm-*.jsep) reject inside ORT's own promise chains,
                // AFTER our app-level catch already degraded gracefully (e.g.
                // vocal-remover falls back and toasts "try a shorter file") —
                // the escaped internal rejection is dashboard noise with no
                // action on our side. Our own failures still surface through
                // our telemetry (tool_error/__handleError), which does not
                // originate from these files. Sentry TIMBRICA-FRONTEND-CE +
                // the earlier Module-load-timeout class (4D).
                /\/vendor\/onnxruntime\//,
                // Check Point ZeroPhishing corporate AV — injects its
                // zp.js (carrying its own globals like `zp_token`) into
                // pages opened in Check Point-managed browsers. When
                // their init script fires before its data block,
                // `zp_token is not defined` escapes to onunhandledrejection
                // on whichever page the user happened to load. Sentry
                // TIMBRICA-FRONTEND-4M. Different from the chrome-extension:
                // protocol filter below — Check Point loads via an HTTPS
                // CDN, so URL pattern is the only handle.
                /zerophishing\.iaas\.checkpoint\.com/,
                /chrome-extension:/,
                /moz-extension:/,
                /safari-extension:/
            ],
            beforeSend(event, hint) {
                // First: nothing after # leaves the page. /private-chat keeps
                // its end-to-end room key in the fragment (#k=…), and the SDK
                // puts location.href into request.url and the navigation
                // breadcrumbs. SentryNoiseFilter.stripUrlFragments covers both;
                // the inline cut is the fallback if that file failed to load.
                try {
                    if (window.SentryNoiseFilter && window.SentryNoiseFilter.stripUrlFragments) window.SentryNoiseFilter.stripUrlFragments(event);
                    else if (event.request && event.request.url) event.request.url = String(event.request.url).split('#')[0];
                } catch(_){}
                // Drop events from anonymous third-party iframes (ads, etc)
                if (event.request && event.request.url && event.request.url.startsWith('about:')) return null;
                // ── Root noise gate ──────────────────────────────────────
                // Drop any exception with not a single stack frame on a
                // timbrica.com origin: extensions, injected privacy scripts
                // (DuckDuckGo content-scope-scripts), ad SDKs, corporate AV.
                // One principled rule — the replacement for growing the
                // per-message ignoreErrors denylist below issue by issue.
                // See public/js/sentry-noise-filter.js. Graceful: if that
                // file failed to load, skip the gate (ignoreErrors backstops).
                try {
                    if (window.SentryNoiseFilter && window.SentryNoiseFilter.isForeignNoise(event)) return null;
                } catch(_){}
                // Sentry FH — sidebar «forEach of undefined», 6 events in 22 days,
                // three fixes made blind: the sidebar's own crash-state string is
                // attached HERE, at capture time. The sidebar cannot do it itself:
                // this handler is registered before its listener and reads the
                // message first, so its suffix never reached us.
                try {
                    if (typeof window.__sbState === 'function' && eventTouches(event, /sidebar(\.min)?\.js|\/js-min\/sidebar/)) {
                        event.extra = Object.assign({}, event.extra, { sidebar: window.__sbState() });
                    }
                } catch(_){}
                // err.expected === true is our contract for "predictable
                // hardware/feature-incompat condition that the throw site
                // already surfaced via __aeToolError + ffmpeg-status / banner".
                // Sentry should never see them — they're product behaviour,
                // not bugs. The unhandledrejection global listener below
                // already preventDefault()s these so Sentry SDK shouldn't
                // even get them, but this is a belt-and-suspenders check
                // for environments where the listener isn't installed yet
                // (very early script load, page-init race).
                //
                // Sentry's SDK does NOT propagate err.expected into
                // event.exception.values[i].mechanism.data — that field is
                // only populated by us in explicit captureException calls.
                // Use the `hint` arg's originalException for the unwrapped
                // Error object instead. The old mechanism.data lookup is
                // kept as a secondary read for backwards compatibility with
                // existing call sites that DO set it via withScope/setExtra.
                // Closes the long tail of Sentry TIMBRICA-FRONTEND-4J / 75 /
                // 72 (vocal-remover audio-buffer alloc fail on multi-minute
                // stereo) which throw with `.expected = true` but kept
                // surfacing in Sentry because the SDK's auto-instrumented
                // event handler caught them before our unhandledrejection
                // listener.
                try {
                    var origErr = hint && hint.originalException;
                    if (origErr && origErr.expected === true) return null;
                    var evVal = event && event.exception && event.exception.values && event.exception.values[0];
                    var mechExp = (evVal && evVal.mechanism && evVal.mechanism.data && evVal.mechanism.data.expected) === true;
                    if (mechExp) return null;
                } catch(_){}
                // Yandex Browser 26 + onnxruntime-web 1.24.x cohort: WebGPU
                // EP build trips a LinkError "function import requires a
                // callable" during WASM instantiate. Our AI fallback chain
                // catches this and silently degrades to the Fast engine
                // (Phase 2.A's broadened condition in computeStandard catch).
                // The cascade noise is real but already handled — don't
                // page anyone. Match by the unique signature of the
                // import name "tb" + Aborted phrase.
                try {
                    var ev0 = event && event.exception && event.exception.values && event.exception.values[0];
                    var msg = ev0 && (ev0.value || ev0.type) || '';
                    if (typeof msg === 'string' && (
                        msg.indexOf('Import #126 "a" "tb"') >= 0 ||
                        (msg.indexOf('LinkError') >= 0 && msg.indexOf('"tb"') >= 0) ||
                        msg.indexOf('previous call to \'initWasm()\' failed') >= 0 ||
                        msg.indexOf('VerifyEachNodeIsAssignedToAnEp') >= 0 ||
                        msg.indexOf('no available backend found. ERR: [webgpu]') >= 0
                    )) return null;
                } catch(_){}
                // Yandex.Direct ads context.js (loaded from yandex.ru/ads/system)
                // tries to detect WKWebView via `window.webkit.messageHandlers`
                // without optional chaining. On Yandex Browser iOS that property
                // is undefined and the cascade fires "undefined is not an
                // object (evaluating 'window.webkit.messageHandlers')". The
                // existing denyUrls regex for `yandex.*` doesn't catch the
                // event because Sentry strips the abs_path down to `[native
                // code]` for native-method forEach calls — no URL to match
                // against. Fingerprint by message + missing frame URLs.
                // Sentry TIMBRICA-FRONTEND-48.
                try {
                    var ev1 = event && event.exception && event.exception.values && event.exception.values[0];
                    var msg1 = ev1 && (ev1.value || '') || '';
                    if (typeof msg1 === 'string' && msg1.indexOf('window.webkit.messageHandlers') >= 0) {
                        var frames = (ev1.stacktrace && ev1.stacktrace.frames) || [];
                        var nativeOnly = frames.length === 0 || frames.every(function(f){
                            var fn = (f && (f.function || '')) || '';
                            var ap = (f && (f.abs_path || f.absPath || f.filename || '')) || '';
                            return !ap || ap === '[native code]' || fn === 'forEach';
                        });
                        if (nativeOnly) return null;
                    }
                } catch(_){}
                // ONNX Runtime Web (onnxruntime-web.min.js) loads its WASM
                // submodules in parallel; on slow networks each fetch can
                // exceed ORT's internal 60-s timeout and fire
                // "Module load timeout: m_XXXX" as an unhandled rejection
                // before our outer .catch() on loadAiModel handles the
                // top-level failure. The user-facing UX is already graceful
                // (vocal-remover falls back to Fast mode with localized
                // hint "AI model unavailable. Using fast mode instead."),
                // so each per-module rejection is dashboard noise rather
                // than a bug. Sentry TIMBRICA-FRONTEND-4D.
                try {
                    var ev2 = event && event.exception && event.exception.values && event.exception.values[0];
                    var msg2 = ev2 && (ev2.value || '') || '';
                    if (typeof msg2 === 'string' && /^Module load timeout: m_\d+/.test(msg2)) return null;
                } catch(_){}
                // ── Patched-member class (third-party injection) ──────────────
                // A frame named like a NATIVE member (document.createElement,
                // Element.getBoundingClientRect, top.addEventListener …) from a
                // file we do not ship proves a wrapper injected over it, and the
                // throw is that wrapper's. The rule and its history live in
                // SentryNoiseFilter.threwInPatchedMember (unit-tested there).
                try {
                    if (window.SentryNoiseFilter && window.SentryNoiseFilter.threwInPatchedMember && window.SentryNoiseFilter.threwInPatchedMember(event)) return null;
                } catch(_){}
                // A failure its OWNER claimed in the same dispatch. The SDK's
                // global handler runs before the owner's listener (which is
                // added while the work runs), so at this point the claim may
                // not have happened yet — the verdict comes one macrotask
                // later, after every listener of that event has run, from the
                // one owner of the question (window.__failureClaimed, layout).
                // Sentry MV (/photo-restore): face-api's shader rejection that
                // detect.js took over and moved to the CPU still became an issue.
                try {
                    var mt = event && event.exception && event.exception.values && event.exception.values[0]
                        && event.exception.values[0].mechanism && event.exception.values[0].mechanism.type;
                    var oe = hint && hint.originalException;
                    if (/onunhandledrejection|onerror/i.test(String(mt || '')) && oe && typeof oe === 'object' && window.__failureClaimed) {
                        return new Promise(function (resolve) {
                            setTimeout(function () {
                                var claimed = false;
                                try { claimed = window.__failureClaimed(null, oe); } catch (_) {}
                                resolve(claimed ? null : event);
                            }, 0);
                        });
                    }
                } catch(_){}
                return event;
            }
        });

        // Early-error replay. This file is DEFERRED (2026-08-06 — it used to be a
        // SYNC <head> tag and gated first paint by ~950 ms), so every inline script
        // on the page has already run by the time we get here. The layout's <head>
        // listener queued anything that threw in that window into window.__earlyErrQ;
        // drain it into Sentry now. Nulling the queue is what stops the head listener
        // from queueing further — from here on Sentry's own global handlers report,
        // so nothing is counted twice.
        try {
            var _earlyQ = window.__earlyErrQ;
            window.__earlyErrQ = null;
            if (_earlyQ && _earlyQ.length) {
                for (var _i = 0; _i < _earlyQ.length; _i++) {
                    var _early = _earlyQ[_i];
                    // Real Error objects keep their stack — capture as exceptions.
                    if (_early instanceof Error) { Sentry.captureException(_early); continue; }
                    if (!_early) continue;

                    // Всё остальное — событие БЕЗ объекта Error. У такого нет ни
                    // стека, ни кадров, поэтому ни один шумовой фильтр ниже его
                    // не рассматривает: они читают event.exception. Так в панель
                    // попадало браузерное уведомление «Not implemented on this
                    // platform» (Safari, /ip-lookup — Sentry DQ) — issue, по
                    // которой нечего чинить и некуда смотреть.
                    //
                    // Судим по ПРОИСХОЖДЕНИЮ, тем же правилом, что и весь
                    // остальной шум: приписать себе можно только то, что пришло
                    // с нашего источника. Уведомление самого браузера источника
                    // не имеет — и это не потеря: send() выше уже записал его в
                    // наш собственный канал tool_error.
                    var _txt = _early, _src = null;
                    if (typeof _early === 'object' && _early.__msg !== undefined) {
                        _txt = _early.__msg;
                        _src = _early.__src || '';
                    }
                    /* ⚠️ Здесь была дыра в этой же правке (22.08). Проверка
                     * происхождения стояла под условием `_src !== null`, то есть
                     * пропускала ВСЁ, у чего происхождения нет ВООБЩЕ.
                     *
                     * А отсутствие происхождения — это и есть неатрибутируемость,
                     * а не повод довериться. Ровно так прошла Sentry G8
                     * (/ru/login, Safari): отвергнутый промис с не-Error причиной
                     * («undefined is not an object (evaluating 'resp.payload')» —
                     * идиома менеджеров паролей, которые как раз влезают на
                     * страницу входа). Путь ошибки я тогда закрыл, а путь
                     * ОТКЛОНЁННОГО ПРОМИСА — нет: он кладёт в очередь сырую
                     * причину, у которой ни стека, ни файла.
                     *
                     * Правило одно для обоих путей: нет объекта Error И нет
                     * нашего источника — issue не заводим. Данные не теряются,
                     * ранний перехватчик уже записал событие в tool_error. */
                    if (_src === null || _src.indexOf(location.origin) !== 0) {
                        try { if (typeof console !== 'undefined' && console.warn) console.warn('[Timbrica] неатрибутируемое сообщение (ни файла, ни объекта):', String(_txt).slice(0, 160)); } catch (_) {}
                        continue;
                    }
                    Sentry.captureMessage(String(_txt).substring(0, 300));
                }
            }
        } catch(_){}

        // Global expected-error sink. Any throw with `err.expected === true`
        // is our product contract for "user-facing hardware/feature-incompat
        // banner already shown, telemetry already recorded — don't surface
        // this as an unhandled rejection or console error". This single
        // listener closes the gap for ALL direct callers of FP.init() (not
        // only engine-warmer's required-engine path). preventDefault()
        // suppresses both the console message and Sentry's auto-capture.
        try {
            window.addEventListener('unhandledrejection', function(e){
                var err = e.reason;
                if (err && err.expected === true) {
                    try { e.preventDefault(); } catch(_){}
                }
            });
            window.addEventListener('error', function(e){
                var err = e && e.error;
                if (err && err.expected === true) {
                    try { e.preventDefault(); } catch(_){}
                }
            });
        } catch(_){}
    }
